import axios from 'axios'
import { camelCase } from 'lodash/string'
import { getCookie } from '../../../utils/cookiesHandler'
import { getCsrfToken } from './security'

const instance = axios.create({
  baseURL: import.meta.env.VITE_API,
  timeout: 180000,
  withCredentials: true,
})

instance.interceptors.request.use(
  // Do something before request is sent
  (config) => {
    return protectByCsrf(config).then((protectedConfig) => protectedConfig);
  },
  // Do something with request error
  (error) => Promise.reject(error),
)

async function protectByCsrf(config) {
  if (!config.csrf) return config;
  let csrfToken = getCookie('__c_token__')

  if (!csrfToken) {
    await getCsrfToken()
    csrfToken = getCookie('__c_token__')
  }

  if (csrfToken) {
    config.headers = {
      'X-CSRFToken': csrfToken,
    };
  }
  return config;
}

/**
 * Parent keys whose nested `options` map is keyed by user authored option labels
 * rather than by field names, however the endpoint happens to name the answer.
 */
const VERBATIM_OPTION_PARENTS = new Set(['answer', 'modelAnswer'])

/**
 * The keys of an answer's `options` map are user authored option labels, not field
 * names. Camelizing them corrupts the label ("o(log n)" -> "oLogN"), which both
 * misreports the model answer and, because the question builder writes the labels it
 * loaded straight back, persists the corrupted label when a question is edited. The
 * map is therefore passed through as is.
 * @param {string} key
 * @param {string|null} parentKey
 * @returns {boolean} true when the value is a map keyed by user content
 */
function isVerbatimKeyMap(key, parentKey) {
  return (
    key === 'options' &&
    parentKey !== null &&
    VERBATIM_OPTION_PARENTS.has(camelCase(parentKey))
  )
}

/**
/**
 * function that returns the object recursively with camel case keys
 * @param {Object} data
 * @param {string|null} [parentKey] key the data was found under
 * @returns {Object} object with camel case keys
 */
function toCamelCase(data, parentKey = null) {
  if (Array.isArray(data)) {
    return data.map((value) => {
      if (value instanceof Object) return toCamelCase(value, parentKey)
      return value
    })
  }
  if (data instanceof Object) {
    const convertedDate = {}
    Object.entries(data).forEach(([key, value]) => {
      convertedDate[camelCase(key)] = isVerbatimKeyMap(key, parentKey)
        ? value
        : toCamelCase(value, key)
    })
    return convertedDate
  }
  // base case
  return data
}

// Add a response interceptor
instance.interceptors.response.use(
  (response) => {
    // Skip transformation for blob responses (file downloads)
    if (response.config.responseType === 'blob' || response.data instanceof Blob) {
      return response
    }

    if (response.results) {
      return toCamelCase(response.results)
    }
    if (response.data) {
      return toCamelCase(response.data)
    }
    return toCamelCase(response)
  },
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  // Do something with response error
  (error) => {
    const msg = error.response?.data?.description || error.response?.data?.message || ''
    if (msg.includes('out of available') || msg.includes('Upgrade the subscription')) {
      window.dispatchEvent(new CustomEvent('subscription:exhausted'))
      const suppressed = new Error()
      suppressed.response = { data: { description: '__SUPPRESS__' } }
      return Promise.reject(suppressed)
    }
    return Promise.reject(error)
  },
)

/** @param {string} resource */
/** @param {object} [config] */
const getAll = (resource, config) => {
  if (config) {
    return instance.get(resource, { ...config })
  }
  return instance.get(resource)
}

/** @param {string} resource */
/** @param {string} id */
/** @param {object} [config] */
const getSingle = (resource, id, config) => {
  if (config) {
    return instance.get(`${resource}/${id}`, { ...config })
  }
  return instance.get(`${resource}/${id}`)
}

/** @param {string} resource */
/** @param {object} model */
/** @param {object} [config] */
const post = (resource, model, config) => {
  if (config) {
    return instance.post(resource, model, { ...config })
  }
  return instance.post(resource, model)
}

/** @param {string} resource */
/** @param {string|number|null|undefined} id */
/** @param {object} model */
/** @param {object} [config] */
const put = (resource, id, model, config) => {
  const url =
    id === null || id === undefined || id === '' ? resource : `${resource}/${id}`
  if (config) {
    return instance.put(url, model, { ...config })
  }
  return instance.put(url, model)
}

/** @param {string} resource */
/** @param {string} id */
/** @param {object} model */
/** @param {object} [config] */
const patch = (resource, id, model, config) => {
  if (config) {
    return instance.patch(`${resource}/${id}`, model, { ...config })
  }
  return instance.patch(`${resource}/${id}`, model)
}

/** @param {string} resource */
/** @param {string} id */
/** @param {object} [config] */
const remove = (resource, id, config) => {
  if (config) {
    return instance.delete(`${resource}/${id}`, { ...config })
  }
  return instance.delete(`${resource}/${id}`)
}

const apiProvider = {
  instance,
  getAll,
  getSingle,
  post,
  put,
  patch,
  remove,
}

export default apiProvider
